import React, { useState, useEffect } from 'react';
import { settingsApi } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  Settings,
  Loader2,
  Save,
  AlertCircle
} from 'lucide-react';

const SettingsPage = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await settingsApi.get();
      setSettings(response.data);
    } catch (error) {
      toast.error('Error al cargar configuración');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await settingsApi.update(settings);
      toast.success('Configuración guardada');
    } catch (error) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (key, value) => {
    setSettings({ ...settings, [key]: value });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-900" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="settings-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Configuración</h1>
          <p className="text-slate-500 mt-1">Parámetros del sistema de inventarios</p>
        </div>
        <Button 
          onClick={handleSave}
          className="bg-blue-900 hover:bg-blue-800"
          disabled={saving}
          data-testid="save-settings-btn"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Guardar Cambios
        </Button>
      </div>

      {/* Inventory Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Configuración de Inventario
          </CardTitle>
          <CardDescription>
            Controla el comportamiento del sistema de inventarios
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-md">
            <div className="space-y-1">
              <Label className="text-base">Permitir stock negativo</Label>
              <p className="text-sm text-slate-500">
                Permite que las existencias de un producto sean menores a cero
              </p>
            </div>
            <Switch
              checked={settings?.allow_negative_stock || false}
              onCheckedChange={(checked) => updateSetting('allow_negative_stock', checked)}
            />
          </div>

          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-md">
            <div className="space-y-1">
              <Label className="text-base">Permitir recibir más de lo ordenado</Label>
              <p className="text-sm text-slate-500">
                Permite recepcionar cantidades mayores a las especificadas en la orden de compra
              </p>
            </div>
            <Switch
              checked={settings?.allow_receive_more_than_ordered || false}
              onCheckedChange={(checked) => updateSetting('allow_receive_more_than_ordered', checked)}
            />
          </div>

          <div className="p-4 bg-slate-50 rounded-md">
            <Label className="text-base mb-3 block">Método de costeo</Label>
            <Select
              value={settings?.cost_method || 'weighted_average'}
              onValueChange={(value) => updateSetting('cost_method', value)}
            >
              <SelectTrigger className="w-full md:w-64">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="weighted_average">Promedio Ponderado</SelectItem>
                <SelectItem value="fifo" disabled>FIFO (Próximamente)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-sm text-slate-500 mt-2">
              Define cómo se calcula el costo de los productos
            </p>
          </div>
        </CardContent>
      </Card>

      {/* MRP Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Configuración MRP</CardTitle>
          <CardDescription>
            Valores predeterminados para las reglas de reabasto
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-2">
              <Label>Método MRP por defecto</Label>
              <Select
                value={settings?.default_mrp_method || 'SIMPLE_MIN_MAX'}
                onValueChange={(value) => updateSetting('default_mrp_method', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="SIMPLE_MIN_MAX">Min/Max Simple</SelectItem>
                  <SelectItem value="AVERAGE_CONSUMPTION">Consumo Promedio</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Tiempo de entrega (días)</Label>
              <Input
                type="number"
                min="0"
                value={settings?.default_lead_time_days || 7}
                onChange={(e) => updateSetting('default_lead_time_days', parseInt(e.target.value))}
              />
              <p className="text-xs text-slate-500">Lead time predeterminado para proveedores</p>
            </div>

            <div className="space-y-2">
              <Label>Días para cálculo de consumo</Label>
              <Input
                type="number"
                min="1"
                value={settings?.default_consumption_days || 30}
                onChange={(e) => updateSetting('default_consumption_days', parseInt(e.target.value))}
              />
              <p className="text-xs text-slate-500">Período para calcular consumo promedio</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="border-blue-200 bg-blue-50/50">
        <CardContent className="p-6 flex gap-4">
          <AlertCircle className="w-6 h-6 text-blue-600 flex-shrink-0" />
          <div>
            <h4 className="font-medium text-blue-900 mb-1">Campos Personalizados</h4>
            <p className="text-sm text-blue-700">
              Los campos personalizados para productos y proveedores se pueden configurar 
              desde esta sección. Esta funcionalidad permite adaptar el sistema a las 
              necesidades específicas de tu empresa sin modificar el código.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SettingsPage;
